import React, { useEffect, useImperativeHandle, useRef } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import useScrollEndsObserver from 'hooks/useScrollEndsObserver'
import Group from 'components/utils/Group'
import { focusStyleOffset } from 'styles/utils/focusStyle'

const FADED_OVERFLOW_WIDTH: number = 48

const Container = styled(Group)`
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent 0,
    black var(--start-fade-width, 0px),
    black calc(100% - var(--end-fade-width, 0px)),
    transparent 100%
  ); /* NOTE: cannot transition mask-image yet! */
  mask-repeat: no-repeat;
  overflow-x: auto;
  scroll-behavior: smooth;
  isolation: isolate;
  padding: ${rem(8)} var(--overflow-gutter, ${focusStyleOffset});
  margin: -${rem(8)} calc(var(--overflow-gutter, ${focusStyleOffset}) * -1);

  &.has-start-faded {
    --start-fade-width: ${rem(FADED_OVERFLOW_WIDTH)};
  }

  &.has-end-faded {
    --end-fade-width: ${rem(FADED_OVERFLOW_WIDTH)};
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

interface Props extends
  React.HTMLAttributes<HTMLDivElement>,
  Pick<React.ComponentProps<typeof Group>, 'gap' | 'tabletGap' | 'desktopGap' | 'horizontalAlign' | 'tabletHorizontalAlign' | 'verticalAlign' | 'tabletVerticalAlign'>,
  React.RefAttributes<HTMLDivElement>
{
  onHasReachedEnd?: (hasReached: boolean) => void
  onHasReachedStart?: (hasReached: boolean) => void
  /**
   * A gutter around the element that allows visibility of content wider
   * than the actual children, e.g. a focus state
   * @default 16 To match layoutContainers default gutter
   */
  overflowGutter?: number
}

function HorizontalContainerWithFadedOverflow(props: Props) {
  const {
    children,
    onHasReachedEnd,
    onHasReachedStart,
    overflowGutter = 16,
    gap,
    tabletGap,
    desktopGap,
    ref,
    verticalAlign,
    tabletVerticalAlign,
    horizontalAlign,
    tabletHorizontalAlign,
    ...restOfProps
  } = props

  const containerRef = useRef<HTMLDivElement | null>(null)

  const {
    hasReachedHorizontalEnd,
    hasReachedHorizontalStart,
  } = useScrollEndsObserver(containerRef, children)

  useEffect(() => {
    onHasReachedStart?.(hasReachedHorizontalStart)
  }, [hasReachedHorizontalStart, onHasReachedStart])

  useEffect(() => {
    onHasReachedEnd?.(hasReachedHorizontalEnd)
  }, [hasReachedHorizontalEnd, onHasReachedEnd])

  useImperativeHandle(ref, () => containerRef.current!)

  return <Group
    direction="horizontal"
    horizontalAlign="stretch"
    {...restOfProps}
  >
    <Container
      direction="horizontal"
      gap={gap}
      tabletGap={tabletGap}
      desktopGap={desktopGap}
      ref={containerRef}
      verticalAlign={verticalAlign}
      tabletVerticalAlign={tabletVerticalAlign}
      horizontalAlign={horizontalAlign}
      tabletHorizontalAlign={tabletHorizontalAlign}
      style={{
        '--overflow-gutter': overflowGutter !== undefined ? rem(overflowGutter) : undefined,
      }}
      className={clsx({
        'has-start-faded': !hasReachedHorizontalStart,
        'has-end-faded': !hasReachedHorizontalEnd,
      })}
    >
      {children}
    </Container>
  </Group>
}

export default HorizontalContainerWithFadedOverflow
